import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import './Baa.css';
import Footer from './Footer';
import LawAppBar from './LawAppBar';

export default function Baa() {

    return (
        <div className="App">
            <LawAppBar />
            <Container
                maxWidth="lg"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px',
                    textAlign: 'left'
                }}
            >
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.2rem' },
                            marginBottom: '1rem',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2
                        }}
                    >
                        BUSINESS ASSOCIATE AGREEMENT
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.2rem' },
                            fontWeight: 400,
                            fontStyle: 'italic'
                        }}
                    >
                        Last Updated: February 4, 2026
                    </Typography>
                    <Button
                        component={Link}
                        to="/execute-baa"
                        variant="contained"
                        sx={{
                            background: 'var(--button-gradient)',
                            color: 'white',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '12px',
                            px: 4,
                            py: 1.5,
                            boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                            textDecoration: 'none',
                            '&:hover': {
                                background: 'var(--button-gradient-hover)',
                                boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)'
                            }
                        }}
                    >
                        Execute BAA
                    </Button>
                </Box>

                {/* Introduction */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        This Business Associate Agreement ("BAA") is entered into by and between Veridical Technologies LLC, doing business as AttorneyAide ("Business Associate"), and the customer executing or accepting this Agreement (the "Customer").
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Customer represents and warrants that it is either:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            (i) a Covered Entity, or
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            (ii) a Business Associate acting on behalf of a Covered Entity,
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        as those terms are defined under the Health Insurance Portability and Accountability Act of 1996 and its implementing regulations ("HIPAA"), and that it is entering into this BAA in connection with its use of the Services involving Protected Health Information ("PHI").
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        This BAA supplements and is incorporated into the parties' applicable service agreement or Terms of Service (the "Agreement").
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        This BAA is effective as of the date Customer first accepts it electronically or first uploads PHI to the Services, whichever occurs first (the "Effective Date").
                    </Typography>
                </Paper>

                {/* Section 1: Purpose and Scope */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        1. Purpose and Scope
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        This BAA is intended to comply with HIPAA, including the HIPAA Privacy Rule, Security Rule, and Breach Notification Rule (collectively, "HIPAA").
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Business Associate may receive, create, maintain, or transmit PHI on behalf of Customer solely for the purpose of providing the services described in the Agreement (the "Services").
                    </Typography>
                </Paper>

                {/* Section 2: Permitted Uses and Disclosures */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        2. Permitted Uses and Disclosures
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Business Associate may use and disclose PHI solely to:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Perform and support the Services as authorized by Customer;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Maintain, secure, and ensure the reliability and functionality of the Services;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Comply with applicable legal obligations; or
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            As otherwise permitted or required by HIPAA.
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Business Associate shall not:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Use PHI for advertising or marketing purposes;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Sell PHI;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Use PHI to train, fine-tune, or improve general-purpose artificial intelligence or machine learning models.
                        </Typography>
                    </Box>
                </Paper>

                {/* Section 3: Safeguards */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        3. Safeguards
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Business Associate shall implement and maintain appropriate administrative, technical, and physical safeguards designed to protect the confidentiality, integrity, and availability of PHI, consistent with HIPAA Security Rule requirements.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Such safeguards include, but are not limited to:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Encryption of PHI in transit and at rest;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Access controls and authentication mechanisms;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Audit logging;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Policies and procedures designed to prevent unauthorized access, use, or disclosure.
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        While Business Associate employs reasonable and industry-standard safeguards, no system can be guaranteed to be completely secure.
                    </Typography>
                </Paper>

                {/* Section 4: User Access Controls and Customer Responsibilities */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        4. User Access Controls and Customer Responsibilities
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Customer is responsible for implementing appropriate administrative safeguards to ensure that access to the Services is limited to authorized workforce members.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Each user account must be assigned to a single authorized individual. Shared credentials are prohibited.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Customer acknowledges that unique user identification is necessary to support audit controls and access tracking under the HIPAA Security Rule (45 C.F.R. § 164.312).
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Customer remains solely responsible for:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Determining whether its use of the Services complies with HIPAA;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Managing workforce access permissions;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Ensuring that only authorized individuals access PHI through the Services.
                        </Typography>
                    </Box>
                </Paper>

                {/* Section 5: Subcontractors */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        5. Subcontractors
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Business Associate may engage subcontractors to assist in providing the Services, provided that any subcontractor that creates, receives, maintains, or transmits PHI agrees in writing to comply with restrictions and safeguards substantially similar to those set forth in this BAA.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Business Associate remains responsible for ensuring that subcontractors appropriately safeguard PHI in accordance with HIPAA.
                    </Typography>
                </Paper>

                {/* Section 6: Breach Notification */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        6. Breach Notification
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Business Associate shall notify Customer without unreasonable delay, and in no event later than sixty (60) days after discovery, of a Breach of Unsecured PHI as defined under HIPAA.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Such notification shall include, to the extent known:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            A description of the breach;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            The types of PHI involved;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Steps taken to mitigate harm; and
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Information reasonably necessary for Customer to comply with its breach notification obligations.
                        </Typography>
                    </Box>
                </Paper>

                {/* Section 7: Access, Amendment, and Accounting */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        7. Access, Amendment, and Accounting
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        To the extent required by HIPAA and applicable to the Services:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Business Associate shall make PHI available to Customer for access requests;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Business Associate shall incorporate amendments to PHI as directed by Customer;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Business Associate shall provide information necessary for Customer to fulfill accounting of disclosure obligations, if applicable.
                        </Typography>
                    </Box>
                </Paper>

                {/* Section 8: Return or Destruction of PHI */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        8. Return or Destruction of PHI
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Upon termination of the Agreement, Business Associate shall, at Customer's option and to the extent feasible:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Return PHI to Customer; or
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Delete or destroy PHI.
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        If return or destruction is not feasible, Business Associate shall continue to protect such PHI in accordance with this BAA and limit further uses and disclosures to those purposes that make return or destruction infeasible.
                    </Typography>
                </Paper>

                {/* Section 9: State Health Privacy Laws */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        9. State Health Privacy Laws
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '12px'
                        }}
                    >
                        (a) Washington My Health My Data Act (MHMDA)
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        To the extent Consumer Health Data (as defined under Washington law) is processed through the Services:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Business Associate acts as a "Processor";
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Business Associate shall process such data only pursuant to documented instructions from Customer;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Business Associate shall not use Consumer Health Data for its own operational or commercial purposes except as strictly necessary to provide and maintain the Services.
                        </Typography>
                    </Box>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '12px'
                        }}
                    >
                        (b) California CCPA/CPRA
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        To the extent applicable:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Business Associate acts as a "Service Provider";
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Business Associate shall not Sell or Share Personal Information as defined under the CCPA/CPRA;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Business Associate shall not retain, use, or disclose Personal Information outside the direct business relationship with Customer.
                        </Typography>
                    </Box>
                </Paper>

                {/* Section 10: Term and Termination */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        10. Term and Termination
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        This BAA shall remain in effect for the duration of the Agreement.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Either party may terminate this BAA if the other party materially breaches its obligations and fails to cure such breach within a reasonable period after written notice.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Termination of this BAA shall not relieve either party of obligations that by their nature survive termination, including confidentiality and PHI protection obligations.
                    </Typography>
                </Paper>

                {/* Section 11: Limitation of Scope */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        11. Limitation of Scope
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px'
                        }}
                    >
                        Nothing in this BAA shall be construed to:
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '24px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Create a partnership, joint venture, or agency relationship;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Expand either party's obligations beyond those required by HIPAA;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Transfer ownership of PHI.
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Customer retains all rights, title, and interest in and to PHI.
                    </Typography>
                </Paper>

                {/* Section 12: Governing Law */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        12. Governing Law
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        This BAA shall be governed by and construed in accordance with the laws of the State of Washington, to the extent not preempted by federal law.
                    </Typography>
                </Paper>
            </Container>
            <Footer />
        </div>
    );
}

